/**
 * Histórico Documental (P37) · búsqueda transversal del acervo documental de
 * las solicitudes de pago + vista previa/descarga/exportación ZIP.
 *
 * Seguridad (spec §9): el frontend NUNCA conoce rutas de almacenamiento — toda
 * recuperación es por DocumentoId; el backend valida pertenencia por sociedad
 * y responde 404 indistinguible cuando el documento no es visible (RN-11).
 */
import { axiosClient } from "@/shared/api/axios-client";
import { encolarDescarga } from "@/shared/api/descarga-secuencial";

export interface ProveedorHistorico {
  numeroAcreedor: number;
  rfc: string;
  razonSocial: string;
}

export interface DocumentoHistorico {
  documentoId: string;
  solicitudId: string;
  numeroSolicitud: number;
  folio: string;
  proveedores: ProveedorHistorico[];
  fechaSolicitud: string;
  nombreArchivo: string;
  clasificacion: string;
  tamanoBytes: number;
  organizacionId: string | null;
  organizacionNombre: string | null;
}

/** Espejo de HistoricoPageDto · mensajeInformativo trae el motivo RN-05/06/07. */
export interface HistoricoPage {
  documentos: DocumentoHistorico[];
  total: number;
  page: number;
  pageSize: number;
  mensajeInformativo: string | null;
}

export interface OpcionHistorico {
  value: string;
  label: string;
}

/** Combos recortados a lo visible (RN-01) + defaults del periodo (RN-04). */
export interface FiltrosHistorico {
  sociedades: OpcionHistorico[];
  clasificaciones: OpcionHistorico[];
  fechaDesdeDefault: string;
  fechaHastaDefault: string;
}

/**
 * Filtros de búsqueda · viven en los search params de la ruta para que la
 * consulta sea compartible por URL. Fechas como "YYYY-MM-DD"; sin fechas el
 * backend aplica el default del mes en curso (RN-04).
 */
export interface HistoricoParams {
  desde?: string;
  hasta?: string;
  rfc?: string;
  acreedor?: string;
  organizacionId?: string;
  clasificacion?: string;
  page?: number;
  pageSize?: number;
}

function buildQueryString(params: HistoricoParams): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export async function obtenerFiltrosHistorico(): Promise<FiltrosHistorico> {
  const { data } = await axiosClient.get<FiltrosHistorico>("/api/historico-documental/filtros");
  return data;
}

export async function buscarHistorico(params: HistoricoParams = {}): Promise<HistoricoPage> {
  const { data } = await axiosClient.get<HistoricoPage>(
    `/api/historico-documental${buildQueryString(params)}`
  );
  return data;
}

/**
 * Vista previa en pestaña nueva · el JWT viaja por el interceptor de axios
 * (window.open directo al API no autenticaría), así que se baja el blob y se
 * abre su objectURL. Se revoca tras un minuto (la pestaña ya lo cargó).
 */
export async function previsualizarDocumento(doc: DocumentoHistorico): Promise<void> {
  // SECUENCIAL por directiva: de una en una, sin paralelas ni ráfagas.
  const response = await encolarDescarga(() =>
    axiosClient.get<Blob>(
      `/api/historico-documental/documentos/${doc.documentoId}/contenido?modo=inline`,
      { responseType: "blob" }
    )
  );
  const url = URL.createObjectURL(response.data);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Descarga vía blob · el filename real viene en `Content-Disposition`. */
async function descargarBlob(response: {
  data: Blob;
  headers: Record<string, unknown>;
}, fallbackFilename: string): Promise<void> {
  const disposition = String(response.headers["content-disposition"] ?? "");
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/.exec(disposition);
  const filename = match?.[1] ? decodeURIComponent(match[1]) : fallbackFilename;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(response.data);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

export async function descargarDocumento(doc: DocumentoHistorico): Promise<void> {
  const response = await encolarDescarga(() =>
    axiosClient.get<Blob>(
      `/api/historico-documental/documentos/${doc.documentoId}/contenido?modo=attachment`,
      { responseType: "blob" }
    )
  );
  await descargarBlob(response, doc.nombreArchivo);
}

/** Exportación por lote (RN-12) · SOLO ids, jamás rutas (spec §9 defecto 3). */
export async function exportarHistoricoZip(documentoIds: string[]): Promise<void> {
  const response = await encolarDescarga(() =>
    axiosClient.post<Blob>(
      "/api/historico-documental/exportar-zip",
      { documentoIds },
      { responseType: "blob" }
    )
  );
  await descargarBlob(response, "Historico.zip");
}
